/**
 * Keeps the marquee's track long enough to loop without going blank.
 *
 * The CSS travels exactly one group per cycle, so the band only stays filled
 * while the track holds that cycle PLUS a whole viewport behind it. Two groups
 * satisfy that only when one group is already as wide as the screen, which no
 * merchant can guarantee for every width: eight short words are 805px against
 * a 1280 viewport, and the tail runs dry mid-loop.
 *
 * So the count is measured, not guessed: clone the set until the track covers
 * a cycle, a viewport and SLACK groups over, and hand the cycle length to CSS
 * in pixels. Without this module the markup keeps its two groups and the CSS
 * falls back to its own percentage shift, which is the behaviour as shipped.
 */

// Whole groups kept beyond the minimum, so a late-loading font or an image
// settling a few pixels wider cannot expose the end of the track.
const SLACK = 2;

class IaMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".ia-marquee__track");
    this.viewport = this.querySelector(".ia-marquee__viewport");
    if (!this.track || !this.viewport) return;

    // The server's second group is already aria-hidden with its links out of
    // the tab order, so copies of it need no further treatment.
    this.source =
      this.track.querySelector('.ia-marquee__group[aria-hidden="true"]') ||
      this.track.querySelector(".ia-marquee__group");
    if (!this.source) return;

    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.fit = this.fit.bind(this);

    this.resize = new ResizeObserver(this.fit);
    this.resize.observe(this.viewport);
    this.motion.addEventListener("change", this.fit);
    // Web fonts land after the first measure and move every width with them.
    document.fonts?.ready.then(this.fit);

    this.fit();
  }

  disconnectedCallback() {
    this.resize?.disconnect();
    this.motion?.removeEventListener("change", this.fit);
  }

  get clones() {
    return this.track.querySelectorAll("[data-ia-marquee-clone]");
  }

  /** Reconciled in place: only trailing clones are added or dropped. */
  fit() {
    if (!this.isConnected) return;

    // Reduced motion draws one static wrapping row, so copies are dead weight.
    if (this.motion.matches) {
      this.clones.forEach((clone) => clone.remove());
      this.style.removeProperty("--ia-marquee-shift");
      return;
    }

    const group = this.source.getBoundingClientRect().width;
    const view = this.viewport.getBoundingClientRect().width;
    // A marquee inside a closed drawer measures zero; the observer calls back
    // when it is given a box.
    if (!group || !view) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const cycle = group + gap;
    const need = Math.ceil((view + gap) / cycle) + 1 + SLACK;
    const have = 2 + this.clones.length;

    for (let i = have; i < need; i += 1) {
      const clone = this.source.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("data-ia-marquee-clone", "");
      this.track.appendChild(clone);
    }
    if (have > need) {
      const clones = Array.from(this.clones);
      clones.slice(need - have).forEach((clone) => clone.remove());
    }

    this.style.setProperty("--ia-marquee-shift", `-${cycle}px`);
  }
}

if (!customElements.get("ia-marquee")) {
  customElements.define("ia-marquee", IaMarquee);
}
